import React, { useState, useCallback, useMemo } from 'react'
import { Eye, EyeOff, Maximize2, Minimize2 } from 'lucide-react'
import { BasePreview } from '../../previews/BasePreview'

interface SplitFormLayoutProps {
  children: React.ReactNode
  preview: React.ReactNode
  previewTitle: string
  className?: string
  onExternalPreview?: () => void
  hideViewControls?: boolean
  hideZoomControls?: boolean
}

export const SplitFormLayout: React.FC<SplitFormLayoutProps> = ({
  children,
  preview,
  previewTitle,
  className = '',
  onExternalPreview,
  hideViewControls = false,
  hideZoomControls = false
}) => {
  const [showPreview, setShowPreview] = useState(true)
  const [isPreviewMaximized, setIsPreviewMaximized] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  // Check if we're on mobile on component mount
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const togglePreview = useCallback(() => {
    setShowPreview(prev => !prev)
  }, [])

  const toggleMaximized = useCallback(() => {
    setIsPreviewMaximized(prev => !prev)
  }, [])

  // Memoize the preview component to prevent unnecessary re-renders
  const memoizedPreview = useMemo(() => (
    <BasePreview
      title={previewTitle}
      onExternalPreview={onExternalPreview}
      hideViewControls={hideViewControls}
      hideZoomControls={hideZoomControls}
    >
      {preview}
    </BasePreview>
  ), [preview, previewTitle, onExternalPreview, hideViewControls, hideZoomControls])

  if (isMobile) {
    // Mobile Layout: Stacked with toggle
    return (
      <div className={`flex flex-col h-full ${className}`}>
        {/* Mobile Header with Preview Toggle */}
        <div className="flex items-center justify-between p-4 bg-white border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">QR-Code erstellen</h2>
          <button
            onClick={togglePreview}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
              showPreview 
                ? 'bg-blue-100 text-blue-700' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            <span className="text-sm">{showPreview ? 'Vorschau ausblenden' : 'Vorschau zeigen'}</span>
          </button>
        </div>

        {/* Mobile Content */}
        <div className="flex-1 overflow-hidden">
          {showPreview ? (
            <div className="h-full">
              {memoizedPreview}
            </div>
          ) : (
            <div className="h-full overflow-auto p-4">
              {children}
            </div>
          )}
        </div>

        {/* Mobile Tab Switcher */}
        {showPreview && (
          <div className="flex bg-white border-t border-gray-200">
            <button
              onClick={() => setShowPreview(false)}
              className="flex-1 py-3 text-center text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
            >
              📝 Formular bearbeiten
            </button>
            <button
              onClick={() => setShowPreview(true)}
              className="flex-1 py-3 text-center text-sm font-medium bg-blue-50 text-blue-700 border-t-2 border-blue-500"
            >
              👁️ Vorschau
            </button>
          </div>
        )}
      </div>
    )
  }

  // Desktop Layout: Side by side
  return (
    <div className={`flex h-full ${className}`}>
      {/* Form Section */}
      <div className="flex-1 overflow-y-auto">

        {/* Form Content */}
        <div className="p-6">
          {children}
        </div>
      </div>

      {/* Preview Section */}
      <div className="w-1/2 bg-gray-50 p-6 overflow-y-auto border-l">
        {memoizedPreview}
      </div>
    </div>
  )
}